import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import { supabase } from '../services/supabase';
import {
  CATALOG,
  CATALOGO_VIGENTE,
  CATALOG_VERSION,
  categoryAvg,
  pillarAvg,
  overall,
  normalizeDetailedSinRelleno,
  withComputedAverages,
  DEFAULT_SCORE,
  totalSubcategories,
  type CategoryScore,
  type DetailedScoresV2,
  type PillarBlock,
} from '../lib/evaluationCatalog';
import { FIELD_POSITIONS } from '../lib/fieldPositions';
import { fmtDate, scoreColor } from '../lib/evaluationFormat';
import {
  findEvaluation,
  saveEvaluationDraft,
  publishEvaluation,
  deleteEvaluationDraft,
  type EvaluationStatus,
} from '../services/evaluationDrafts';
import { hoy } from '../lib/fechaLocal';

// Formulario de evaluación formal n=8 — extraído de MonitorEvaluacionesPage
// (C1 de la Entrega 2) SIN cambiar comportamiento.
//
// POR QUÉ SE EXTRAE
//
// La pantalla de dirección que crea informes a demanda necesita exactamente
// este formulario: 60 subcategorías, comentarios por pilar, posiciones de campo
// y el ciclo de objetivos. Estaba inline en la página del entrenador, así que o
// se extraía o se copiaba. Ya hay una copia a mano de este formulario en la app
// (MonitorEvaluationsScreen), y ésa no se puede evitar —React Native no comparte
// primitivas con la web—, pero una tercera dentro del mismo árbol sí.
//
// QUÉ CAMBIA RESPECTO A LO QUE HABÍA: nada de comportamiento. El guardado sigue
// siendo el mismo `.upsert` con el mismo `onConflict`, el mismo ciclo de
// objetivos y el mismo gating de 60/60. Los `data-testid` se conservan uno a uno
// para que los tests que ya existen sigan valiendo de red.
//
// LO ÚNICO QUE SE PARAMETRIZA es quién FIRMA el informe (`monitorId`). Antes era
// siempre `profile.id` porque solo lo abría el propio entrenador; dirección
// elegirá el entrenador y lo pasará por aquí.

export interface EvaluationFormProps {
  studentId: string;
  studentName: string;
  /** Entrenador que FIRMA el informe. No tiene por qué ser quien lo teclea. */
  monitorId: string;
  periodStart: string;
  periodEnd: string;
  onBack: () => void;
  /** Se llama tras un guardado correcto (la página recarga su cola). */
  onSaved?: () => void;
  /** Texto del encabezado. Por defecto, el de la pantalla del entrenador. */
  title?: string;
}

interface PrevObjective {
  id: string;
  pillar: string;
  description: string;
}
interface NewObjective {
  pillar: string;
  description: string;
}

// El formulario PUNTÚA, y se puntúa siempre con el catálogo vigente: un
// informe nuevo nace en la versión de hoy. Para LEER uno guardado, el catálogo
// es el de su fila (ver `loadEval`).
const TOTAL_SUBS = totalSubcategories(CATALOGO_VIGENTE);

// EL PRIMER PILAR DEL CATÁLOGO VIGENTE, NO UNO ESCRITO A MANO.
//
// Aquí ponía `'tecnico'` en cuatro sitios: el pilar que se abre al entrar y el
// que trae por defecto un objetivo nuevo. Con el v3 esa clave ya no existe, así
// que el formulario abría todo colapsado y —peor— un objetivo nuevo nacía con
// un pilar que no está en el catálogo.
const PILAR_POR_DEFECTO = CATALOG[0].key;
const MAX_NEW_OBJECTIVES = 5;
// Estados de cierre de un objetivo (D7). "maintained" se clona como pending.
const OBJ_STATUSES: { key: string; label: string }[] = [
  { key: 'achieved', label: 'Conseguido' },
  { key: 'partial', label: 'Parcial' },
  { key: 'not_achieved', label: 'No conseguido' },
  { key: 'maintained', label: 'Mantenido' },
];
const PILLAR_LABEL: Record<string, string> = Object.fromEntries(
  CATALOG.map((p) => [p.key, p.label])
);

function round1(n: number) {
  return Number(n.toFixed(1));
}
const subKey = (p: string, c: string, s: string) => `${p}.${c}.${s}`;

// Construye el detailed_scores (3 niveles) a partir del mapa plano de subs
// puntuadas. Solo incluye las subs presentes; categoría/pilar se computan con los
// helpers de N8-1 (0 = sin datos).
//
// Un BORRADOR se guarda tal cual sale de aquí: lo que nadie ha puntuado no se
// escribe. Solo el informe ya PUBLICADO pasa por `withComputedAverages`, donde
// las 44 están y rellenar no inventa nada.
function composeDetailed(
  subScores: Record<string, number>,
  pillarComments: Record<string, string>
): DetailedScoresV2 {
  const ds: DetailedScoresV2 = { catalog_version: CATALOG_VERSION };
  for (const p of CATALOG) {
    const categories: Record<string, CategoryScore> = {};
    for (const c of p.categories) {
      const subs: Record<string, { score: number }> = {};
      for (const s of c.subs) {
        const v = subScores[subKey(p.key, c.key, s.key)];
        if (typeof v === 'number') subs[s.key] = { score: v };
      }
      categories[c.key] = {
        score: round1(
          categoryAvg(
            { score: 0, subs },
            c.subs.map((s) => s.key)
          )
        ),
        subs,
      };
    }
    ds[p.key] = {
      score: round1(pillarAvg({ score: 0, comment: '', categories }, p.categories)),
      comment: pillarComments[p.key] ?? '',
      categories,
    };
  }
  return ds;
}

// Aplana un detailed_scores normalizado a (subs puntuadas + comentarios de pilar)
// para cargar una evaluación existente en el form.
function flattenDetailed(ds: DetailedScoresV2): {
  subScores: Record<string, number>;
  pillarComments: Record<string, string>;
} {
  const subScores: Record<string, number> = {};
  const pillarComments: Record<string, string> = {};
  for (const p of CATALOG) {
    const block = ds[p.key];
    if (typeof block !== 'object') continue;
    pillarComments[p.key] = block.comment ?? '';
    for (const c of p.categories) {
      const cat = block.categories?.[c.key];
      for (const s of c.subs) {
        const v = cat?.subs?.[s.key]?.score;
        if (typeof v === 'number') subScores[subKey(p.key, c.key, s.key)] = v;
      }
    }
  }
  return { subScores, pillarComments };
}

export default function EvaluationForm({
  studentId,
  studentName,
  monitorId,
  periodStart,
  periodEnd,
  onBack,
  onSaved,
  title = 'Evaluación formal · n=8',
}: EvaluationFormProps) {
  const [subScores, setSubScores] = useState<Record<string, number>>({});
  const [pillarComments, setPillarComments] = useState<Record<string, string>>({});
  const [overallComment, setOverallComment] = useState('');
  // PJ-3 — Posición de campo (snapshot por informe). Claves EN del catálogo de 24.
  const [positionPrimary, setPositionPrimary] = useState<string | null>(null);
  const [positionSecondary, setPositionSecondary] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set([PILAR_POR_DEFECTO]));
  const [evalExists, setEvalExists] = useState(false);
  // Identidad y estado del informe cargado. `evalStatus` decide qué botones se
  // pintan: un informe ya publicado se sigue editando como siempre (mig 128);
  // uno nuevo o en borrador va por las RPC de la 195.
  const [evalId, setEvalId] = useState<string | null>(null);
  const [evalStatus, setEvalStatus] = useState<EvaluationStatus | null>(null);
  const [reportNumber, setReportNumber] = useState<string | null>(null);
  const [loadingEval, setLoadingEval] = useState(false);
  // La versión del borrador encontrado, si NO es la vigente. null = todo normal.
  const [catalogoNoSoportado, setCatalogoNoSoportado] = useState<number | null>(null);
  // ⚠️ El formulario NO se pinta si no se pudo comprobar si ya existe un
  // informe. Empezar uno nuevo vacía las 60 subcategorías, y hacerlo sobre un
  // borrador que sí está es perder el trabajo de alguien al primer guardado.
  const [errorEval, setErrorEval] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState('');
  const [errMsg, setErrMsg] = useState('');

  // Objetivos EV2 (D7).
  const [prevObjectives, setPrevObjectives] = useState<PrevObjective[]>([]);
  const [objReviews, setObjReviews] = useState<Record<string, { status: string; comment: string }>>(
    {}
  );
  const [newObjectives, setNewObjectives] = useState<NewObjective[]>([
    { pillar: PILAR_POR_DEFECTO, description: '' },
  ]);

  // Medias vivas: se recomputan desde las subs puntuadas en cada cambio.
  const liveDetailed = useMemo(
    () => composeDetailed(subScores, pillarComments),
    [subScores, pillarComments]
  );
  const scoredCount = Object.keys(subScores).length;
  const canFinalize = scoredCount >= TOTAL_SUBS;
  const globalAvg = overall(liveDetailed, CATALOGO_VIGENTE);

  const loadEval = useCallback(async () => {
    setLoadingEval(true);
    setErrorEval(null);
    setCatalogoNoSoportado(null);
    const [encontrado, { data: pendingObjs, error: errorObjs }] = await Promise.all([
      findEvaluation(studentId, monitorId, periodStart),
      supabase.rpc('get_pending_objectives_for_student', { p_student_id: studentId }),
    ]);

    // Los objetivos pendientes tampoco son adorno: de ellos sale la revisión
    // que se firma en el informe. Si no se leen, decir que no hay ninguno es
    // cerrar en falso los que el alumno tenía abiertos.
    if (encontrado.error || errorObjs) {
      setErrorEval(encontrado.error ?? errorObjs?.message ?? 'Error desconocido');
      setLoadingEval(false);
      return;
    }

    const data = encontrado.informe;
    setEvalId(data?.id ?? null);
    setEvalStatus(data?.status ?? null);
    setReportNumber(data?.report_number ?? null);

    if (data) {
      // UN BORRADOR DE OTRO CATÁLOGO NO SE ABRE EN ESTE FORMULARIO.
      //
      // Aquí se normalizaba contra el catálogo vigente. Con una sola versión
      // viva eso da lo mismo, pero en cuanto haya dos: las claves del borrador
      // no existen en el catálogo nuevo, `flattenDetailed` no encuentra
      // ninguna, el entrenador ve el formulario NUEVO EN BLANCO sobre un
      // borrador que sí tiene trabajo dentro, y al guardar lo sobrescribe
      // —porque el upsert sella `catalog_version` sin condición—.
      //
      // Desde el v3 esto YA puede pasar: un borrador empezado con el v2 y
      // abierto con el v3 vigente.
      // Sin versión, la fila es VIEJA (la columna existe desde el v2), así que
      // cae al 2 y el guard de abajo la rechaza. Caer al vigente la abriría en
      // blanco y el guardado se la llevaría.
      const versionDelBorrador = data.catalog_version ?? 2;
      if (versionDelBorrador !== CATALOG_VERSION) {
        setCatalogoNoSoportado(versionDelBorrador);
        setEvalExists(true);
        setLoadingEval(false);
        return;
      }
      // SIN RELLENO. `normalizeDetailed` pone DEFAULT_SCORE en toda sub que
      // falte, y eso abriría una asignación recién creada como «44/44
      // puntuadas» con «Publicar» habilitado: 44 cincos que nadie puso. Lo que
      // se abre es lo que hay escrito (runbook §126).
      const { subScores: sc, pillarComments: pc } = flattenDetailed(
        normalizeDetailedSinRelleno(data.detailed_scores, CATALOGO_VIGENTE)
      );
      setSubScores(sc);
      setPillarComments(pc);
      setOverallComment(data.overall_comment ?? '');
      setPositionPrimary((data.position_primary as string | null) ?? null);
      setPositionSecondary((data.position_secondary as string | null) ?? null);
      setEvalExists(true);
    } else {
      setSubScores({});
      setPillarComments({});
      setOverallComment('');
      setPositionPrimary(null);
      setPositionSecondary(null);
      setEvalExists(false);
    }

    const pend = (pendingObjs ?? []) as { id: string; pillar: string; description: string }[];
    setPrevObjectives(
      pend.map((o) => ({ id: o.id, pillar: o.pillar, description: o.description }))
    );
    const initReviews: Record<string, { status: string; comment: string }> = {};
    pend.forEach((o) => {
      initReviews[o.id] = { status: 'achieved', comment: '' };
    });
    setObjReviews(initReviews);
    setNewObjectives([{ pillar: PILAR_POR_DEFECTO, description: '' }]);
    setLoadingEval(false);
  }, [studentId, monitorId, periodStart]);

  useEffect(() => {
    setExpanded(new Set([PILAR_POR_DEFECTO]));
    setSavedMsg('');
    setErrMsg('');
    loadEval();
  }, [loadEval]);

  // ── Edición de subs y comentarios ──
  function setSub(pillarKey: string, catKey: string, subKeyName: string, score: number) {
    const clamped = Math.min(10, Math.max(1, score));
    setSubScores((prev) => ({ ...prev, [subKey(pillarKey, catKey, subKeyName)]: clamped }));
  }
  function stepSub(pillarKey: string, catKey: string, subKeyName: string, delta: number) {
    const cur = subScores[subKey(pillarKey, catKey, subKeyName)];
    // Primera interacción sobre una sub sin puntuar → la fija en DEFAULT_SCORE.
    setSub(pillarKey, catKey, subKeyName, typeof cur === 'number' ? cur + delta : DEFAULT_SCORE);
  }
  function setPillarComment(pillarKey: string, comment: string) {
    setPillarComments((prev) => ({ ...prev, [pillarKey]: comment }));
  }

  // ── Objetivos nuevos ──
  function addNewObjective() {
    setNewObjectives((prev) =>
      prev.length >= MAX_NEW_OBJECTIVES
        ? prev
        : [...prev, { pillar: PILAR_POR_DEFECTO, description: '' }]
    );
  }
  function removeNewObjective(idx: number) {
    setNewObjectives((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  }

  // ── Guardado ────────────────────────────────────────────────────
  // Hay DOS caminos, y la diferencia no es cosmética:
  //
  //  · Informe NUEVO o en BORRADOR → RPC de la mig 195. Guardar y publicar son
  //    actos distintos; el ciclo de objetivos lo cierra el servidor al publicar.
  //  · Informe YA PUBLICADO        → el upsert de siempre, intacto. Editar un
  //    informe entregado es un flujo que ya existía y no se toca en esta
  //    entrega; cambiarlo aquí sería arrastrar un cambio de comportamiento que
  //    nadie ha pedido.

  const isPublished = evalStatus === 'published';

  async function persistDraft(): Promise<string | null> {
    const res = await saveEvaluationDraft({
      studentId,
      monitorId,
      periodStart,
      periodEnd,
      // `composeDetailed` a secas: SOLO las subs puntuadas. Pasarlo por
      // `withComputedAverages` rellenaría el resto con DEFAULT_SCORE y el
      // borrador quedaría escrito en la base como un informe entero —y el gate
      // del servidor, que cuenta las subs de la fila, lo dejaría publicar.
      detailedScores: composeDetailed(subScores, pillarComments),
      overallComment: overallComment.trim() || null,
      positionPrimary,
      positionSecondary,
      catalogVersion: CATALOG_VERSION,
    });
    if (!res.ok) {
      setErrMsg(res.message ?? 'No se pudo guardar el borrador');
      return null;
    }
    setEvalId(res.evaluationId ?? null);
    setEvalStatus('draft');
    setEvalExists(true);
    return res.evaluationId ?? null;
  }

  async function handleSaveDraft() {
    setSaving(true);
    setErrMsg('');
    const id = await persistDraft();
    setSaving(false);
    if (!id) return;
    setSavedMsg(`✓ Borrador de ${studentName} guardado`);
    setTimeout(() => setSavedMsg(''), 3000);
    onSaved?.();
  }

  async function handlePublish() {
    if (!canFinalize) return;
    setSaving(true);
    setErrMsg('');
    // Se guarda antes de publicar para que lo que se entrega sea lo que hay en
    // pantalla, no lo que se guardó la última vez.
    const id = await persistDraft();
    if (!id) {
      setSaving(false);
      return;
    }
    const reviews = prevObjectives.map((o) => ({
      id: o.id,
      status: objReviews[o.id]?.status ?? 'achieved',
      comment: objReviews[o.id]?.comment ?? '',
    }));
    const res = await publishEvaluation(id, reviews, newObjectives);
    setSaving(false);
    if (!res.ok) {
      setErrMsg(res.message ?? 'No se pudo publicar el informe');
      return;
    }
    setEvalStatus('published');
    setReportNumber(res.reportNumber ?? null);
    setSavedMsg(`✓ Informe ${res.reportNumber ?? ''} publicado y enviado a ${studentName}`);
    onSaved?.();
  }

  async function handleDeleteDraft() {
    if (!evalId) return;
    if (
      !window.confirm(
        `¿Borrar el borrador de ${studentName}? Se pierde lo puntuado hasta ahora y no se puede deshacer.`
      )
    ) {
      return;
    }
    setSaving(true);
    setErrMsg('');
    const res = await deleteEvaluationDraft(evalId);
    setSaving(false);
    if (!res.ok) {
      setErrMsg(res.message ?? 'No se pudo borrar el borrador');
      return;
    }
    onSaved?.();
    onBack();
  }

  async function handleSavePublished() {
    if (!canFinalize) return;
    setSaving(true);
    setErrMsg('');
    const detailed = withComputedAverages(
      composeDetailed(subScores, pillarComments),
      CATALOGO_VIGENTE
    );
    const { data: saved, error } = await supabase
      .from('formal_evaluations')
      .upsert(
        {
          student_id: studentId,
          monitor_id: monitorId,
          period_start: periodStart,
          period_end: periodEnd,
          evaluation_date: hoy(),
          detailed_scores: detailed,
          overall_comment: overallComment.trim() || null,
          position_primary: positionPrimary,
          position_secondary: positionSecondary,
          catalog_version: CATALOG_VERSION,
        },
        { onConflict: 'student_id,monitor_id,period_start' }
      )
      .select('id')
      .single();

    if (error || !saved) {
      setSaving(false);
      setErrMsg(error?.message ?? 'No se pudo guardar la evaluación');
      return;
    }
    const savedId = saved.id;

    // Cierre de objetivos previos (+ clonado de los "mantenidos").
    // La evaluación de arriba SÍ comprobaba su error; estas escrituras no, así
    // que unos objetivos podían quedarse sin cerrar y el formulario anunciaba
    // "✓ Evaluación guardada" igual. Como van en paralelo, se recogen todos
    // los resultados y basta uno malo para no cantar victoria.
    const objErrores = (
      await Promise.all(
        prevObjectives.map(async (o) => {
          const r = objReviews[o.id];
          if (!r) return null;
          const { error: updError } = await supabase
            .from('evaluation_objectives')
            .update({
              status: r.status,
              evaluation_comment: r.comment.trim() || null,
              evaluated_in_eval_id: savedId,
            })
            .eq('id', o.id);
          if (updError) return updError.message;
          if (r.status === 'maintained') {
            const { error: cloneError } = await supabase.from('evaluation_objectives').insert({
              student_id: studentId,
              created_in_eval_id: savedId,
              pillar: o.pillar,
              description: o.description,
              status: 'pending',
            });
            if (cloneError) return cloneError.message;
          }
          return null;
        })
      )
    ).filter((m): m is string => m !== null);

    if (objErrores.length) {
      setSaving(false);
      setErrMsg(
        `La evaluación se guardó, pero ${objErrores.length} objetivo(s) no: ${objErrores[0]}`
      );
      return;
    }

    // Objetivos nuevos (con texto; pilar obligatorio, máx 5).
    const toInsert = newObjectives
      .filter((o) => o.description.trim())
      .map((o) => ({
        student_id: studentId,
        created_in_eval_id: savedId,
        pillar: o.pillar,
        description: o.description.trim(),
        status: 'pending',
      }));
    if (toInsert.length) {
      const { error: newObjError } = await supabase.from('evaluation_objectives').insert(toInsert);
      // La evaluación ya está guardada; lo que falta son los objetivos nuevos.
      // Decirlo así evita que el entrenador los dé por puestos.
      if (newObjError) {
        setSaving(false);
        setErrMsg(`La evaluación se guardó, pero los objetivos nuevos no: ${newObjError.message}`);
        return;
      }
    }

    setSaving(false);
    setEvalExists(true);
    setSavedMsg(`✓ Evaluación de ${studentName} guardada`);
    setTimeout(() => setSavedMsg(''), 3000);
    onSaved?.();
  }

  // Un borrador de otro catálogo: no se abre ni se ofrece guardar. Pintar el
  // formulario de hoy encima sería enseñar en blanco lo que sí tiene notas, y
  // el primer guardado se lo llevaría.
  if (catalogoNoSoportado !== null) {
    return (
      <div className="max-w-3xl mx-auto space-y-4" data-testid="eval-form-catalogo-no-soportado">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gold-primary font-inter text-sm hover:opacity-80"
        >
          ← Volver a la lista
        </button>
        <div className="card border border-error/40 bg-error/5 p-6">
          <p className="font-oswald text-white text-lg">Este informe es de otro catálogo</p>
          <p className="font-inter text-text-secondary text-sm mt-2">
            Se empezó con el catálogo <span className="font-mono">v{catalogoNoSoportado}</span> y
            este formulario puntúa con el <span className="font-mono">v{CATALOG_VERSION}</span>. No
            se abre aquí: las notas que ya tiene no se corresponden con los apartados de ahora, y
            guardarlo encima las borraría.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="eval-form">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gold-primary font-inter text-sm hover:opacity-80"
      >
        ← Volver a la lista
      </button>

      <div>
        <h2 className="font-oswald text-2xl text-white">{title}</h2>
        <p className="font-oswald text-lg text-gold-primary mt-1">{studentName}</p>
        <p className="font-inter text-text-secondary text-sm">
          Periodo: {fmtDate(periodStart)} → {fmtDate(periodEnd)}
        </p>
        {evalExists &&
          (isPublished ? (
            <p
              className="font-inter text-[#4CAF50] text-xs mt-1"
              data-testid="eval-state-published"
            >
              ✓ Publicado{reportNumber ? ` · ${reportNumber}` : ''} — editando
            </p>
          ) : (
            <p className="font-inter text-warning text-xs mt-1" data-testid="eval-state-draft">
              ● Borrador — el alumno todavía no lo ve
            </p>
          ))}
      </div>

      {errorEval ? (
        <div
          className="text-error text-sm py-8 text-center space-y-3"
          data-testid="eval-form-error"
        >
          <p>
            No se ha podido comprobar si ya existe un informe de este alumno para el periodo. No se
            abre el formulario: empezar uno nuevo encima de un borrador que exista borraría lo que
            tuviera dentro.
          </p>
          <p className="text-text-secondary text-xs">{errorEval}</p>
          <button
            className="btn-ghost text-xs"
            onClick={loadEval}
            data-testid="eval-form-reintentar"
          >
            Reintentar
          </button>
        </div>
      ) : loadingEval ? (
        <div className="text-gold-primary font-oswald animate-pulse py-8 text-center">
          Cargando evaluación...
        </div>
      ) : (
        <>
          {/* Cabecera: progreso + media global viva */}
          <div className="bg-bg-surface border border-bg-border rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="font-inter text-xs text-text-secondary uppercase">Progreso</p>
              <p className="font-oswald text-lg text-white" data-testid="progress">
                {scoredCount}/{TOTAL_SUBS} puntuadas
              </p>
            </div>
            <div className="text-right">
              <p className="font-inter text-xs text-text-secondary uppercase">Media global</p>
              <p
                className="font-oswald text-2xl"
                style={{ color: scoreColor(globalAvg) }}
                data-testid="global-avg"
              >
                {globalAvg > 0 ? globalAvg.toFixed(1) : '–'}
              </p>
            </div>
          </div>

          {/* PJ-3 — Posición de campo (principal + secundaria), snapshot del informe */}
          <div
            className="bg-bg-surface border border-bg-border rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
            data-testid="position-picker"
          >
            <label className="font-inter text-xs text-text-secondary block">
              Posición principal
              <select
                value={positionPrimary ?? ''}
                onChange={(e) => setPositionPrimary(e.target.value || null)}
                data-testid="position-primary"
                className="mt-1 w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
              >
                <option value="">— Sin asignar —</option>
                {FIELD_POSITIONS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label} ({p.key})
                  </option>
                ))}
              </select>
            </label>
            <label className="font-inter text-xs text-text-secondary block">
              Posición secundaria (opcional)
              <select
                value={positionSecondary ?? ''}
                onChange={(e) => setPositionSecondary(e.target.value || null)}
                data-testid="position-secondary"
                className="mt-1 w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
              >
                <option value="">— Sin asignar —</option>
                {FIELD_POSITIONS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label} ({p.key})
                  </option>
                ))}
              </select>
            </label>
          </div>

          {CATALOG.map((pillar) => {
            const isOpen = expanded.has(pillar.key);
            const block = liveDetailed[pillar.key] as PillarBlock;
            const pAvg = block.score;
            return (
              <div
                key={pillar.key}
                className="bg-bg-surface border border-bg-border rounded-xl overflow-hidden"
                data-testid={`pillar-${pillar.key}`}
              >
                <button
                  onClick={() =>
                    setExpanded((prev) => {
                      const n = new Set(prev);
                      if (n.has(pillar.key)) n.delete(pillar.key);
                      else n.add(pillar.key);
                      return n;
                    })
                  }
                  data-testid={`pillar-toggle-${pillar.key}`}
                  className="w-full flex items-center justify-between px-4 py-3"
                >
                  <span className="font-oswald text-white">{pillar.label}</span>
                  <span className="flex items-center gap-3">
                    <span
                      className="font-oswald text-sm"
                      style={{ color: scoreColor(pAvg) }}
                      data-testid={`pillar-${pillar.key}-avg`}
                    >
                      {pAvg > 0 ? pAvg.toFixed(1) : '–'}
                    </span>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </span>
                </button>

                {isOpen && (
                  <div className="px-4 pb-4 space-y-4">
                    {pillar.categories.map((cat) => {
                      const catScore = block.categories[cat.key].score;
                      return (
                        <div key={cat.key} className="border-t border-bg-border pt-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-oswald text-sm text-white">{cat.label}</span>
                            <span
                              className="font-oswald text-sm"
                              style={{ color: scoreColor(catScore) }}
                              data-testid={`cat-${cat.key}-avg`}
                            >
                              {catScore > 0 ? catScore.toFixed(1) : '–'}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {cat.subs.map((s) => {
                              const val = subScores[subKey(pillar.key, cat.key, s.key)];
                              const scored = typeof val === 'number';
                              return (
                                <div
                                  key={s.key}
                                  className="flex items-center justify-between gap-2"
                                >
                                  <span className="font-inter text-sm text-text-secondary">
                                    {s.label}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => stepSub(pillar.key, cat.key, s.key, -1)}
                                      className="w-6 h-6 rounded bg-bg-elevated text-white"
                                      data-testid={`sub-${cat.key}-${s.key}-dec`}
                                      aria-label={`bajar ${s.label}`}
                                    >
                                      −
                                    </button>
                                    <span
                                      className="font-oswald w-6 text-center"
                                      style={{ color: scored ? scoreColor(val) : '#505050' }}
                                      data-testid={`sub-${cat.key}-${s.key}-score`}
                                    >
                                      {scored ? val : '–'}
                                    </span>
                                    <button
                                      onClick={() => stepSub(pillar.key, cat.key, s.key, 1)}
                                      className="w-6 h-6 rounded bg-bg-elevated text-white"
                                      data-testid={`sub-${cat.key}-${s.key}-inc`}
                                      aria-label={`subir ${s.label}`}
                                    >
                                      +
                                    </button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}

                    {/* Comentario de pilar (uno de los dos niveles de comentario) */}
                    <div className="border-t border-bg-border pt-3">
                      <label className="font-inter text-xs text-text-secondary block mb-1">
                        Comentario de {pillar.label}
                      </label>
                      <textarea
                        value={pillarComments[pillar.key] ?? ''}
                        onChange={(e) => setPillarComment(pillar.key, e.target.value)}
                        maxLength={1000}
                        rows={2}
                        data-testid={`pillar-comment-${pillar.key}`}
                        className="w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Comentario global */}
          <div className="bg-bg-surface border border-bg-border rounded-xl p-4">
            <label className="font-inter text-sm text-white block">
              Comentario general
              <textarea
                value={overallComment}
                onChange={(e) => setOverallComment(e.target.value)}
                maxLength={1000}
                rows={3}
                data-testid="overall-comment"
                className="mt-1 w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
              />
            </label>
          </div>

          {/* Objetivos EV2 — revisión de los anteriores */}
          {prevObjectives.length > 0 && (
            <div
              className="bg-bg-surface border border-bg-border rounded-xl p-4 space-y-3"
              data-testid="objectives-review"
            >
              <h3 className="font-oswald text-white">Revisión de objetivos anteriores</h3>
              {prevObjectives.map((o) => {
                const r = objReviews[o.id] ?? { status: 'achieved', comment: '' };
                return (
                  <div
                    key={o.id}
                    className="border border-bg-border rounded-lg p-3 space-y-2"
                    data-testid={`review-${o.id}`}
                  >
                    <p className="font-inter text-xs text-gold-primary">
                      {PILLAR_LABEL[o.pillar] ?? o.pillar}
                    </p>
                    <p className="font-inter text-sm text-white">{o.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {OBJ_STATUSES.map((st) => (
                        <button
                          key={st.key}
                          data-testid={`review-${o.id}-${st.key}`}
                          onClick={() =>
                            setObjReviews((prev) => ({
                              ...prev,
                              [o.id]: { ...prev[o.id], status: st.key },
                            }))
                          }
                          className={`px-3 py-1 rounded-lg font-inter text-xs ${
                            r.status === st.key
                              ? 'bg-gold-primary text-black'
                              : 'bg-bg-elevated text-text-secondary'
                          }`}
                        >
                          {st.label}
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={r.comment}
                      onChange={(e) =>
                        setObjReviews((prev) => ({
                          ...prev,
                          [o.id]: { ...prev[o.id], comment: e.target.value },
                        }))
                      }
                      placeholder="Comentario (opcional)"
                      data-testid={`review-comment-${o.id}`}
                      className="w-full bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
                      rows={2}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {/* Objetivos EV2 — nuevos para el próximo periodo */}
          <div
            className="bg-bg-surface border border-bg-border rounded-xl p-4 space-y-3"
            data-testid="objectives-new"
          >
            <h3 className="font-oswald text-white">Objetivos próximas ~8 sesiones</h3>
            {newObjectives.map((o, idx) => (
              <div
                key={idx}
                className="border border-bg-border rounded-lg p-3 space-y-2"
                data-testid={`new-objective-row-${idx}`}
              >
                <div className="flex flex-wrap gap-2">
                  {CATALOG.map((p) => (
                    <button
                      key={p.key}
                      data-testid={`new-obj-pillar-${idx}-${p.key}`}
                      onClick={() =>
                        setNewObjectives((prev) =>
                          prev.map((x, i) => (i === idx ? { ...x, pillar: p.key } : x))
                        )
                      }
                      className={`px-3 py-1 rounded-lg font-inter text-xs ${
                        o.pillar === p.key
                          ? 'bg-gold-primary text-black'
                          : 'bg-bg-elevated text-text-secondary'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    value={o.description}
                    onChange={(e) =>
                      setNewObjectives((prev) =>
                        prev.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x))
                      )
                    }
                    placeholder="Describe el objetivo"
                    data-testid={`new-objective-${idx}`}
                    className="flex-1 bg-bg-elevated border border-bg-border rounded p-2 text-sm text-white"
                  />
                  {newObjectives.length > 1 && (
                    <button
                      onClick={() => removeNewObjective(idx)}
                      data-testid={`remove-objective-${idx}`}
                      className="text-[#FF4444] p-1"
                      aria-label="eliminar objetivo"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {newObjectives.length < MAX_NEW_OBJECTIVES && (
              <button
                onClick={addNewObjective}
                data-testid="add-objective"
                className="flex items-center gap-2 text-gold-primary font-inter text-sm"
              >
                <Plus size={16} /> Añadir objetivo
              </button>
            )}
          </div>

          {errMsg && <p className="text-[#FF4444] font-inter text-sm">{errMsg}</p>}
          {savedMsg && <p className="text-[#4CAF50] font-inter text-sm">{savedMsg}</p>}
          {!canFinalize && (
            <p className="text-text-secondary font-inter text-xs" data-testid="finalize-hint">
              Puntúa las {TOTAL_SUBS} subcategorías para finalizar ({scoredCount}/{TOTAL_SUBS}).
            </p>
          )}
          {isPublished ? (
            // Informe ya entregado: se sigue editando como siempre.
            <button
              onClick={handleSavePublished}
              disabled={saving || !canFinalize}
              data-testid="save-eval"
              className="w-full bg-gold-primary text-black font-oswald rounded-xl py-3 disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          ) : (
            <div className="space-y-2">
              {/* Guardar NO exige el catálogo completo: para eso está el
                  borrador. Publicar sí, y lo vuelve a exigir el servidor. */}
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                data-testid="save-draft"
                className="w-full bg-bg-elevated border border-bg-border text-white font-oswald rounded-xl py-3 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar borrador'}
              </button>
              <button
                onClick={handlePublish}
                disabled={saving || !canFinalize}
                data-testid="save-eval"
                className="w-full bg-gold-primary text-black font-oswald rounded-xl py-3 disabled:opacity-50"
              >
                {saving ? 'Publicando...' : 'Publicar informe'}
              </button>
              <p className="text-text-secondary font-inter text-xs text-center">
                Al publicar, el alumno recibe un aviso y ya no se puede volver a borrador.
              </p>
              {evalId && (
                <button
                  onClick={handleDeleteDraft}
                  disabled={saving}
                  data-testid="delete-draft"
                  className="w-full text-[#FF4444] font-inter text-sm py-2 disabled:opacity-50"
                >
                  Borrar borrador
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
